import { Injectable, signal } from '@angular/core'
import { AppError, ErrorCode } from '../../interfaces/error.interface'

export interface Toast {
  id: string
  type: 'error' | 'warning' | 'success' | 'info'
  message: string
  duration?: number
}

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  #toasts = signal<Toast[]>([])
  toasts = this.#toasts.asReadonly()

  #lastError = signal<AppError | null>(null)
  lastError = this.#lastError.asReadonly()

  showError(message: string, duration = 5000) {
    console.log('mensaje a mostrar', message)
    this.addToast('error', message, duration)
  }

  showWarning(message: string, duration = 4000) {
    this.addToast('warning', message, duration)
  }

  showSuccess(message: string, duration = 3000) {
    this.addToast('success', message, duration)
  }

  showInfo(message: string, duration = 3000) {
    this.addToast('info', message, duration)
  }

  private addToast(type: Toast['type'], message: string, duration: number) {
    const id = crypto.randomUUID()
    const toast: Toast = { id, type, message, duration }

    this.#toasts.update(toasts => [...toasts, toast])

    if (duration > 0) {
      setTimeout(() => this.removeToast(id), duration)
    }
  }

  removeToast(id: string) {
    this.#toasts.update(toasts => toasts.filter(t => t.id !== id))
  }

  clearToasts() {
    this.#toasts.set([])
  }

  handleError(error: unknown, showToast = true): AppError {
    const appError = this.parseError(error)
    this.#lastError.set(appError)

    if (showToast) {
      this.showError(appError.message)
    }

    this.logError(appError)
    return appError
  }

  private parseError(error: unknown): AppError {
    if (this.isHttpError(error)) {
      return this.parseHttpError(error)
    }

    if (error instanceof Error) {
      return {
        code: 'UNKNOWN_ERROR',
        message: error.message,
        timestamp: new Date()
      }
    }

    return {
      code: 'UNKNOWN_ERROR',
      message: 'Ha ocurrido un error inesperado',
      timestamp: new Date()
    }
  }

  private isHttpError(error: unknown): error is { status: number; error?: { message?: string } } {
    return typeof error === 'object' && error !== null && 'status' in error
  }

  private parseHttpError(error: { status: number; error?: { message?: string } }): AppError {
    const status = error.status
    const serverMessage = error.error?.message

    const errorMap: Record<number, { code: ErrorCode; message: string }> = {
      0: { code: 'NETWORK_ERROR', message: 'Error de conexión. Verifica tu internet.' },
      401: { code: 'UNAUTHORIZED', message: 'Sesión expirada. Por favor inicia sesión.' },
      403: { code: 'FORBIDDEN', message: 'No tienes permisos para realizar esta acción.' },
      404: { code: 'NOT_FOUND', message: 'El recurso solicitado no existe.' },
      500: { code: 'SERVER_ERROR', message: 'Error del servidor. Intenta más tarde.' }
    }

    const mapped = errorMap[status] || { code: 'UNKNOWN_ERROR', message: 'Ha ocurrido un error' }

    return {
      code: mapped.code,
      message: serverMessage || mapped.message,
      details: `HTTP ${status}`,
      timestamp: new Date()
    }
  }

  private logError(error: AppError) {
    console.error(`[${error.code}] ${error.message}`, error)
  }
}
